import styles from './ComposeGuard.module.css';

/** Draft intercepted by the compose guard, held until the user picks a
 *  destination. Kept whole (files + mentions) so nothing is lost either way. */
export interface PendingDraft {
  content: string;
  files?: File[];
  mentions?: string[];
}

interface ComposeGuardProps {
  /** Display name of the thread owner (roster name, falling back to username). */
  ownerName: string;
  /** Non-null while a first-send confirm is pending for this thread. */
  pending: PendingDraft | null;
  /** "Post to {owner}'s thread" — proceed with the held draft in place. */
  onPostAnyway: () => void;
  /** "Start my own thread" — create a fresh thread and carry the draft over. */
  onStartOwnThread: () => void;
  /** True while the new thread is being created (disables both buttons). */
  busy?: boolean;
}

/**
 * Shown above the input whenever the active thread is NOT owned by the
 * current user (or ownership is still unknown — we fail toward warning).
 *
 * Two layers:
 * 1. A compact persistent banner reminding the user whose thread this is.
 * 2. On the FIRST send attempt per thread per browser session, an inline
 *    confirm offering to redirect the draft into a fresh thread instead.
 *    There is deliberately NO dismiss/cancel button: both choices deliver the
 *    message somewhere, so the typed draft can never be silently lost.
 */
export function ComposeGuard({ ownerName, pending, onPostAnyway, onStartOwnThread, busy }: ComposeGuardProps) {
  return (
    <div className={styles.guard}>
      <div className={styles.banner} role="note">
        <span className={styles.bannerIcon} aria-hidden>&#x1F465;</span>
        <span>
          This is <strong>{ownerName}</strong>&rsquo;s thread &mdash; they&rsquo;ll see what you write.
        </span>
      </div>
      {pending && (
        <div className={styles.confirm} role="alertdialog" aria-label="Confirm where to post">
          <div className={styles.confirmText}>
            Post this in {ownerName}&rsquo;s thread, or keep it in a thread of your own?
          </div>
          {pending.content.trim() && (
            <div className={styles.draftPreview}>&ldquo;{truncate(pending.content, 140)}&rdquo;</div>
          )}
          <div className={styles.confirmActions}>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={onStartOwnThread}
              disabled={busy}
            >
              {busy ? 'Creating…' : 'Start my own thread'}
            </button>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={onPostAnyway}
              disabled={busy}
            >
              Post to {ownerName}&rsquo;s thread
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function truncate(text: string, max: number): string {
  const t = text.trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}
